import { Badge, Checkbox } from '@cloudflare/kumo';
import { useState, type ReactNode } from 'react';
import { DataTable as Table } from './ui/DataTable';
import { Contact } from '../types';
import { formatPhoneNumber } from '../utils/phoneUtils';
import { DangerConfirmModal } from './DangerConfirmModal';
import { RowActions } from './atlas/RowActions';
import { SortHeader, SortState } from './atlas/DataTableParts';
import { InlineEdit } from './atlas/InlineEdit';
import { SkeletonTable } from './ui/Skeleton';

export type ContactSortKey = 'nome' | 'telefone' | 'email' | 'atualizadoEm';

interface ContactListProps {
  contacts: Contact[];
  loading: boolean;
  canEdit: boolean;
  sort: SortState<ContactSortKey>;
  onSort: (s: SortState<ContactSortKey>) => void;
  onEdit: (contact: Contact) => void;
  onDelete: (id: string) => void;
  onManageTags: (contact: Contact) => void;
  onPatch: (contact: Contact, data: { nome?: string; email?: string }) => Promise<void>;
  onViewHistory?: (contact: Contact) => void;
  onCopy: (text: string, what: string) => void;
  selectedContactIds: string[];
  onToggleSelect: (id: string) => void;
  onSelectAll: () => void;
  emptyMessage?: string;
}

function formatDate(dateString: string) {
  return new Date(dateString).toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });
}

/** Tabela de contatos no padrão do Atlas: seleção, ordenação, edição na linha, tags e menu "…" */
export function ContactList({
  contacts, loading, canEdit, sort, onSort, onEdit, onDelete, onManageTags, onPatch, onViewHistory, onCopy,
  selectedContactIds, onToggleSelect, onSelectAll, emptyMessage,
}: ContactListProps) {
  // Exclusão só depois de digitar DELETE ou o nome do contato
  const [pendingDelete, setPendingDelete] = useState<{ id: string; nome: string } | null>(null);

  if (loading && contacts.length === 0) {
    return (
      <SkeletonTable rows={8} cols={5} label="Carregando contatos" />
    );
  }

  if (contacts.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-16 text-center">
        <div className="w-14 h-14 rounded-full flex items-center justify-center mb-4" style={{ backgroundColor: 'var(--tint-green)' }}>
          <svg className="w-7 h-7" style={{ color: 'var(--brand-text)' }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
          </svg>
        </div>
        <p className="!text-[var(--text-muted)]">{emptyMessage || 'Nenhum contato encontrado.'}</p>
      </div>
    );
  }

  const selectedOnPage = contacts.filter((c) => selectedContactIds.includes(c.id)).length;
  const allSelected = selectedOnPage === contacts.length;

  // O Cloudscape não deixa aplicar classe na linha da tabela: o realce da linha
  // selecionada vai no conteúdo da célula.
  const cell = (contact: Contact, content: ReactNode) => {
    const selected = selectedContactIds.includes(contact.id);
    return (
      <div
        className={selected ? 'atlas-row--selected' : undefined}
        style={selected ? { background: 'var(--tint-blue)' } : undefined}
      >
        {content}
      </div>
    );
  };

  const columnDefinitions = [
    {
      id: 'selecao',
      header: (
        <Checkbox onCheckedChange={() => onSelectAll()} aria-label={allSelected ? 'Desmarcar os contatos desta página' : 'Selecionar os contatos desta página'} checked={allSelected} indeterminate={selectedOnPage > 0 && !allSelected} />
      ),
      cell: (contact: Contact) => cell(contact, (
        <Checkbox onCheckedChange={() => onToggleSelect(contact.id)} aria-label={`Selecionar ${contact.nome}`} checked={selectedContactIds.includes(contact.id)} />
      )),
    },
    {
      id: 'nome',
      header: <SortHeader label="Nome" sortKey="nome" sort={sort} onSort={onSort} />,
      cell: (contact: Contact) => cell(contact, (
        <InlineEdit
          label="Nome"
          value={contact.nome}
          disabled={!canEdit}
          onSave={(nome) => onPatch(contact, { nome })}
          render={(v) => <p className="font-medium !text-[var(--text)]">{v}</p>}
        />
      )),
    },
    {
      id: 'telefone',
      header: <SortHeader label="Telefone" sortKey="telefone" sort={sort} onSort={onSort} />,
      cell: (contact: Contact) => cell(contact, (
        <>
          <p className="!text-[var(--text-muted)] font-mono">{formatPhoneNumber(contact.telefone)}</p>
          {contact.consentAt && (
            <span title={`Consentimento em ${new Date(contact.consentAt).toLocaleString('pt-BR')}${contact.consentSource ? ` (${contact.consentSource})` : ''}`}><Badge variant="green" className="mt-1">Opt-in</Badge></span>
          )}
          {contact.bsuid && (
            <span title={`ID da API oficial: ${contact.bsuid}`}><Badge variant="blue" className="mt-1">ID Meta</Badge></span>
          )}
        </>
      )),
    },
    {
      id: 'email',
      header: <SortHeader label="E-mail" sortKey="email" sort={sort} onSort={onSort} />,
      cell: (contact: Contact) => cell(contact, (
        <InlineEdit
          label="E-mail"
          type="email"
          placeholder="nome@empresa.com"
          value={contact.email || ''}
          disabled={!canEdit}
          onSave={(email) => onPatch(contact, { email })}
          render={(v) => <p className="!text-[var(--text-muted)]">{v || '—'}</p>}
        />
      )),
    },
    {
      id: 'tags',
      header: 'Tags',
      cell: (contact: Contact) => cell(contact, (
        (contact.tags || []).length === 0 ? (
          canEdit ? <button type="button" className="atlas-link" onClick={() => onManageTags(contact)}>+ Adicionar tags</button> : <span className="atlas-muted">—</span>
        ) : (
          <button type="button" className="atlas-tags atlas-tags--button" onClick={() => onManageTags(contact)} disabled={!canEdit} title={canEdit ? 'Gerenciar tags' : (contact.tags || []).join(', ')}>
            {(contact.tags || []).slice(0, 3).map((t) => <Badge variant="neutral" key={t} className="atlas-tag">{t}</Badge>)}
            {(contact.tags || []).length > 3 && <span className="atlas-muted">+{(contact.tags || []).length - 3}</span>}
          </button>
        )
      )),
    },
    {
      id: 'categoria',
      header: 'Categoria',
      cell: (contact: Contact) => cell(contact, (
        contact.categoria ? (
          <Badge variant="blue" >
            {contact.categoria.nome}
          </Badge>
        ) : (
          <p className="!text-[var(--text-faint)]">—</p>
        )
      )),
    },
    {
      id: 'atualizadoEm',
      header: <SortHeader label="Atualizado em" sortKey="atualizadoEm" sort={sort} onSort={onSort} />,
      cell: (contact: Contact) => cell(contact, (
        <p className="!text-[var(--text-muted)] text-xs">{formatDate(contact.atualizadoEm)}</p>
      )),
    },
    {
      id: 'acoes',
      header: 'Ações',
      cell: (contact: Contact) => cell(contact, (
        <RowActions
          label={contact.nome}
          actions={[
            ...(canEdit ? [{ label: 'Editar contato', onClick: () => onEdit(contact) }, { label: 'Gerenciar tags', onClick: () => onManageTags(contact) }] : []),
            ...(onViewHistory ? [{ label: 'Ver histórico de mensagens', onClick: () => onViewHistory(contact) }] : []),
            { label: 'Copiar telefone', onClick: () => onCopy(contact.telefone, 'Telefone') },
            { label: 'Copiar ID do contato', onClick: () => onCopy(contact.id, 'ID do contato') },
          ]}
          onDelete={canEdit ? () => setPendingDelete({ id: contact.id, nome: contact.nome }) : undefined}
        />
      )),
    },
  ];

  return (
    <div className="overflow-x-auto" aria-busy={loading}>
      <Table
        items={contacts}
        columnDefinitions={columnDefinitions}
        trackBy={(contact: Contact) => contact.id}
        wrapLines
        ariaLabels={{
          selectionGroupLabel: 'Contatos',
          tableLabel: 'Contatos',
        }}
      />
      <DangerConfirmModal
        open={pendingDelete !== null}
        title={`Excluir o contato "${pendingDelete?.nome ?? ''}"?`}
        confirmText="Excluir"
        objectName={pendingDelete?.nome}
        onCancel={() => setPendingDelete(null)}
        onConfirm={() => { const t = pendingDelete; setPendingDelete(null); if (t) onDelete(t.id); }}
      >
        O contato e o histórico dele nesta organização são apagados. Não dá para desfazer.
      </DangerConfirmModal>
    </div>
  );
}
